import { moment, companyTz, companyTimeFormat, formatCivilDate } from '@/libs/companyTime'

export const CHART_LABEL_ALTERNATE_THRESHOLD = 90
export const CHART_MONTHLY_THRESHOLD = 365
/** When the chart card is narrower than this, denser ranges get compact labels + scroll. */
export const CHART_COMPACT_MAX_WIDTH = 992
/** On narrow cards, denser ranges get compact labels + horizontal scroll. */
export const CHART_COMPACT_LABEL_THRESHOLD = 30
/** Minimum pixel width per category when scrolling on compact viewports. */
export const CHART_COMPACT_MIN_POINT_WIDTH = 32

export function getDateRangeDayCount(from, to) {
  return moment(to, 'YYYY-MM-DD').diff(moment(from, 'YYYY-MM-DD'), 'days') + 1
}

/** Uses the chart card width (not user-agent). */
export function isCompactDashboardWidth(width) {
  return Number(width) > 0 && Number(width) < CHART_COMPACT_MAX_WIDTH
}

/** How many category indexes to skip between visible x-axis labels. */
export function getChartLabelStep(categoryCount, { compact = false, xaxisTitle = '' } = {}) {
  if (compact) {
    if (categoryCount <= CHART_COMPACT_LABEL_THRESHOLD) return 1
    if (categoryCount <= 45) return 3
    if (categoryCount <= 60) return 4
    if (categoryCount <= 90) return 5
    if (categoryCount <= 180) return 7
    return 14
  }

  if (xaxisTitle === 'Hours') return 2
  return categoryCount > CHART_LABEL_ALTERNATE_THRESHOLD ? 2 : 1
}

export function thinChartCategories(categories, step) {
  if (!Array.isArray(categories) || !step || step <= 1) {
    return categories
  }

  const last = categories.length - 1
  return categories.map((label, index) => (
    index % step === 0 || index === last ? label : ''
  ))
}

export function getCompactChartMinWidth(categoryCount) {
  return Math.max(categoryCount * CHART_COMPACT_MIN_POINT_WIDTH, 0)
}

export function buildDashboardChartView({ days, from, to, series, granularity = 'day' }) {
  if (granularity === 'hour') {
    return buildHourlyView(days, from, series)
  }

  const dayCount = getDateRangeDayCount(from, to)

  if (dayCount > CHART_MONTHLY_THRESHOLD) {
    return buildMonthlyView(days, from, to, series)
  }

  return buildDailyView(days, series)
}

function formatHourLabel(hour) {
  const m = moment.tz(companyTz()).startOf('day').add(hour, 'hours')
  return companyTimeFormat() === '24h' ? m.format('HH:mm') : m.format('h A')
}

function buildHourlyView(hours, from, series) {
  const dateLabel = formatCivilDate(from)
  const categories = hours.map((row) => formatHourLabel(row.hour))

  return {
    categories,
    tooltipLabels: hours.map((row) => `${dateLabel} ${formatHourLabel(row.hour)}`),
    series: series.map((item) => ({
      name: item.name,
      data: hours.map((row) => Number(row[item.key] ?? 0)),
    })),
    xaxisTitle: 'Hours',
  }
}

function buildDailyView(days, series) {
  const tooltipLabels = days.map((row) => formatCivilDate(row.date))
  const categories = days.map((row) => moment(row.date, 'YYYY-MM-DD').format('MMM D'))

  return {
    categories,
    tooltipLabels,
    series: series.map((item) => ({
      name: item.name,
      data: days.map((row) => Number(row[item.key] ?? 0)),
    })),
    xaxisTitle: 'Days',
  }
}

function buildMonthlyView(days, from, to, series) {
  const spansMultipleYears = moment(from, 'YYYY-MM-DD').year() !== moment(to, 'YYYY-MM-DD').year()
  const monthBuckets = {}

  const current = moment(from, 'YYYY-MM-DD').startOf('month')
  const end = moment(to, 'YYYY-MM-DD').startOf('month')

  while (current.isBefore(end) || current.isSame(end, 'month')) {
    const key = current.format('YYYY-MM')
    monthBuckets[key] = {
      label: spansMultipleYears ? current.format('MMM YYYY') : current.format('MMM'),
      sums: Object.fromEntries(series.map((item) => [item.key, 0])),
    }
    current.add(1, 'month')
  }

  days.forEach((row) => {
    const key = moment(row.date, 'YYYY-MM-DD').format('YYYY-MM')

    if (!monthBuckets[key]) {
      return
    }

    series.forEach((item) => {
      monthBuckets[key].sums[item.key] += Number(row[item.key] ?? 0)
    })
  })

  const orderedKeys = Object.keys(monthBuckets).sort()

  return {
    categories: orderedKeys.map((key) => monthBuckets[key].label),
    tooltipLabels: orderedKeys.map((key) => monthBuckets[key].label),
    series: series.map((item) => ({
      name: item.name,
      data: orderedKeys.map((key) => roundAmount(monthBuckets[key].sums[item.key])),
    })),
    xaxisTitle: 'Months',
  }
}

function roundAmount(value) {
  return Math.round(Number(value) * 100) / 100
}

export function buildDashboardTooltipXFormatter(tooltipLabels) {
  return {
    formatter(value, { dataPointIndex }) {
      return tooltipLabels[dataPointIndex] ?? value
    },
  }
}

export function buildDashboardXaxisLabels({ compact = false } = {}) {
  if (compact) {
    return {
      rotate: -45,
      rotateAlways: true,
      hideOverlappingLabels: true,
      trim: false,
      maxHeight: 90,
      style: {
        fontSize: '10px',
      },
    }
  }

  return {
    rotate: -45,
    hideOverlappingLabels: true,
  }
}

export function buildDashboardChartOptions({
  chartView,
  chartType = 'line',
  colors = [],
  yAxisTitle = '',
  yAxisFormatter = (value) => value,
  tooltipYFormatter = null,
  sharedTooltip = false,
  extraOptions = {},
}) {
  if (!chartView) {
    return {}
  }

  const baseOptions = {
    chart: {
      type: chartType,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    colors,
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      // Full labels — DashboardChartCard thins them by viewport.
      categories: chartView.categories,
      title: {
        text: chartView.xaxisTitle,
      },
      labels: buildDashboardXaxisLabels(),
    },
    yaxis: {
      title: {
        text: yAxisTitle,
      },
      labels: {
        formatter: yAxisFormatter,
      },
    },
    tooltip: {
      shared: sharedTooltip || chartType === 'line',
      // Shared tooltips follow the x position; single-series bars still require intersecting the column.
      intersect: !(sharedTooltip || chartType === 'line'),
      x: buildDashboardTooltipXFormatter(chartView.tooltipLabels),
      y: {
        formatter: tooltipYFormatter || yAxisFormatter,
      },
    },
  }

  if (chartType === 'bar') {
    baseOptions.plotOptions = {
      bar: {
        borderRadius: 4,
        columnWidth: '55%',
      },
    }
  }

  if (chartType === 'line') {
    // Keep bar plotOptions present — ApexCharts can read plotOptions.bar even for line charts.
    baseOptions.plotOptions = {
      bar: {
        borderRadius: 4,
        columnWidth: '55%',
      },
    }
    baseOptions.stroke = {
      curve: 'straight',
      width: 3,
    }
    baseOptions.markers = {
      size: 4,
      hover: {
        size: 6,
      },
    }
    baseOptions.legend = {
      position: 'top',
    }
  }

  return {
    ...baseOptions,
    ...extraOptions,
  }
}

export function buildDashboardChartViewFromStatistics({
  statistics,
  selectedDateRange,
  series,
}) {
  const { from, to } = selectedDateRange || {}

  if (!from || !to || !statistics?.days) {
    return null
  }

  return buildDashboardChartView({
    days: statistics.days,
    from,
    to,
    granularity: statistics.granularity,
    series,
  })
}
